// The receiver card of the Início: how many connections are working, waiting on the holder at the
// transmitter, or failed, read off the connection list's record count, with the way to the list.
import { useQueries } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Option, Result } from 'effect';
import { useState } from 'react';
import type { PowerDeploymentProfile } from '@finnest/power-admin-contracts';
import type { OpinReceiverConnectionStatus } from '@finnest/power-admin-contracts/opin-receiver';
import { MetricTile } from '@finnest/ui';
import { connectionCountQueryOptions } from '../../receptora/client/edge.ts';
import { STATUS_LABELS, failureRefusal } from '../../receptora/view/format.ts';
import { ErrorState, LoadingState, StaleDataNotice, failedAtOf } from '../../ui/states.tsx';

// The receiver connections the operator follows from here: those working, those waiting on the
// holder at the transmitter, and those that failed.
const RECEIVER_COUNTED: readonly OpinReceiverConnectionStatus[] = [
  'active',
  'awaiting_auth',
  'error',
];
const RECEIVER_SUBJECT = 'as conexões da receptora';

export const receives = ({ ecosystems: { opin } }: PowerDeploymentProfile): boolean =>
  opin.status === 'enabled' && opin.participantRole !== 'transmitter';

const sameCounts = (left: readonly number[], right: readonly number[] | undefined): boolean =>
  right !== undefined && left.every((count, index) => count === right[index]);

const ReceiverCounts = (): JSX.Element => {
  const queries = useQueries({
    queries: RECEIVER_COUNTED.map((status) => connectionCountQueryOptions(status)),
  });
  const failed = queries.filter(
    (query) =>
      query.status === 'error' || (query.data !== undefined && Result.isFailure(query.data)),
  );
  const counts = queries.flatMap((query) =>
    query.data !== undefined && Result.isSuccess(query.data) ? [query.data.success] : [],
  );
  const latest = counts.length === RECEIVER_COUNTED.length ? counts : undefined;
  // The last full set of counts, kept so a refresh that fails leaves them on screen as stale.
  const [kept, setKept] = useState<readonly number[]>();
  if (latest !== undefined && !sameCounts(latest, kept)) setKept(latest);
  const shown = latest ?? kept;
  const retry = {
    onRetry: (): void => {
      for (const query of failed) void query.refetch();
    },
    retrying: failed.some((query) => query.isFetching),
    subject: RECEIVER_SUBJECT,
    failedAt: failedAtOf(failed),
    // The first failed count that names Power's trace.
    refusal: Option.firstSomeOf(
      failed.map((query) =>
        Option.fromUndefinedOr(query.data).pipe(
          Option.flatMap(Result.getFailure),
          Option.flatMap(failureRefusal),
        ),
      ),
    ),
    traceable: true,
  };

  if (shown === undefined) {
    return failed.length > 0 ? (
      <ErrorState {...retry} reason={Option.none()} />
    ) : (
      <LoadingState label={RECEIVER_SUBJECT} layout="section" />
    );
  }
  return (
    <>
      {failed.length > 0 ? <StaleDataNotice {...retry} /> : null}
      <div className="admin-metric-grid">
        {RECEIVER_COUNTED.map((status, index) => (
          <MetricTile key={status} label={STATUS_LABELS[status]} value={shown[index]} />
        ))}
      </div>
    </>
  );
};

export const ReceiverCard = (): JSX.Element => (
  <section aria-labelledby="inicio-receptora">
    <h2 id="inicio-receptora">Receptora — conexões</h2>
    <ReceiverCounts />
    <Link to="/receptora" className="fui-button fui-button-secondary">
      Ver as conexões da receptora
    </Link>
  </section>
);
